import type { z } from "zod/v4";
import {
  interpolateResource,
  queryToSearchParams,
  type AnyResourceConfig,
  type BasePathParams,
  type QuerySchema,
  type ResourceConfig,
  type ResourceParams,
  type ResourceScope,
  type TenantOnlyPathParams,
} from "./resource";
import { buildClientApiPath, buildFeaturePath, ClientApiFeaturePathParams, TenantPathParams } from "./index";

// ==================== HTTP Methods ====================

/** HTTP methods supported by the client API. PATCH is intentionally omitted — use PUT. */
export type HttpMethod = "GET" | "POST" | "PUT" | "DELETE";
/** Methods that carry a request body. */
export type MethodWithBody = "POST" | "PUT";
/**
 * Methods that do not carry a request body. DELETE is included here because the
 * client encodes DELETE parameters into the query string rather than the body
 * (see `mutationFor` for the rationale).
 */
export type MethodWithoutBody = "GET" | "DELETE";

// ==================== Endpoint Config ====================

/**
 * Base endpoint config fields.
 */
interface EndpointConfigBase<
  TResource extends ResourceConfig<string, z.ZodType<unknown, ResourceParams<string>>, ResourceScope>,
  TResponseSchema extends z.ZodType,
  TQuerySchema extends QuerySchema | undefined,
> {
  resource: TResource;
  responseSchema: TResponseSchema;
  querySchema?: TQuerySchema;
}

/**
 * Endpoint config for methods that support a body (POST, PUT).
 */
interface EndpointConfigWithBody<
  TResource extends ResourceConfig<string, z.ZodType<unknown, ResourceParams<string>>, ResourceScope>,
  TMethod extends MethodWithBody,
  TResponseSchema extends z.ZodType,
  TBodySchema extends z.ZodType,
  TQuerySchema extends QuerySchema | undefined,
> extends EndpointConfigBase<TResource, TResponseSchema, TQuerySchema> {
  method: TMethod;
  bodySchema: TBodySchema;
}

/**
 * Endpoint config for methods that don't support a body (GET, DELETE).
 */
interface EndpointConfigWithoutBody<
  TResource extends ResourceConfig<string, z.ZodType<unknown, ResourceParams<string>>, ResourceScope>,
  TMethod extends MethodWithoutBody,
  TResponseSchema extends z.ZodType,
  TQuerySchema extends QuerySchema | undefined,
> extends EndpointConfigBase<TResource, TResponseSchema, TQuerySchema> {
  method?: TMethod;
}

/**
 * Full endpoint configuration.
 * If method is POST or PUT, bodySchema is required.
 * If method is GET or DELETE (or omitted, defaulting to GET), bodySchema is not allowed.
 */
export type EndpointConfig<
  TResource extends ResourceConfig<string, z.ZodType<unknown, ResourceParams<string>>, ResourceScope>,
  TMethod extends HttpMethod,
  TResponseSchema extends z.ZodType,
  TBodySchema extends z.ZodType | undefined,
  TQuerySchema extends QuerySchema | undefined = undefined,
> =
  TMethod extends MethodWithBody ?
    TBodySchema extends z.ZodType ?
      EndpointConfigWithBody<TResource, TMethod, TResponseSchema, TBodySchema, TQuerySchema>
    : never
  : EndpointConfigWithoutBody<TResource, TMethod & MethodWithoutBody, TResponseSchema, TQuerySchema>;

/**
 * Simplified endpoint config type for general use.
 */
export type AnyEndpointConfig = EndpointConfig<
  AnyResourceConfig,
  HttpMethod,
  z.ZodType,
  z.ZodType | undefined,
  QuerySchema | undefined
>;

/**
 * Endpoint config constrained to GET method (for queries).
 */
export type GetEndpointConfig = EndpointConfig<AnyResourceConfig, "GET", z.ZodType, undefined, QuerySchema | undefined>;

/**
 * Endpoint config constrained to mutation methods (POST, PUT, DELETE).
 */
export type MutationEndpointConfig = EndpointConfig<
  AnyResourceConfig,
  "POST" | "PUT" | "DELETE",
  z.ZodType,
  z.ZodType | undefined,
  QuerySchema | undefined
>;

// ==================== defineEndpoint ====================

/**
 * Defines an endpoint - a resource combined with request/response schemas.
 *
 * @example
 * // GET endpoint (default)
 * const listSnapshots = defineEndpoint(snapshotsResource, {
 *   responseSchema: ListSnapshotsResponseSchema,
 * });
 *
 * // GET endpoint with query params
 * const listClips = defineEndpoint(clipsResource, {
 *   responseSchema: ListClipsResponseSchema,
 *   querySchema: ListClipsQueryParams,
 * });
 *
 * // POST endpoint with body
 * const createSnapshot = defineEndpoint(snapshotsResource, {
 *   method: "POST",
 *   bodySchema: CreateSnapshotBodySchema,
 *   responseSchema: CreateSnapshotResponseSchema,
 * });
 */
export function defineEndpoint<
  TResource extends ResourceConfig<string, z.ZodType<unknown, ResourceParams<string>>, ResourceScope>,
  TResponseSchema extends z.ZodType,
  TQuerySchema extends QuerySchema | undefined = undefined,
>(
  resource: TResource,
  options: { responseSchema: TResponseSchema; querySchema?: TQuerySchema },
): EndpointConfig<TResource, "GET", TResponseSchema, undefined, TQuerySchema>;

export function defineEndpoint<
  TResource extends ResourceConfig<string, z.ZodType<unknown, ResourceParams<string>>, ResourceScope>,
  TMethod extends MethodWithoutBody,
  TResponseSchema extends z.ZodType,
  TQuerySchema extends QuerySchema | undefined = undefined,
>(
  resource: TResource,
  options: { method: TMethod; responseSchema: TResponseSchema; querySchema?: TQuerySchema },
): EndpointConfig<TResource, TMethod, TResponseSchema, undefined, TQuerySchema>;

export function defineEndpoint<
  TResource extends ResourceConfig<string, z.ZodType<unknown, ResourceParams<string>>, ResourceScope>,
  TMethod extends MethodWithBody,
  TResponseSchema extends z.ZodType,
  TBodySchema extends z.ZodType,
  TQuerySchema extends QuerySchema | undefined = undefined,
>(
  resource: TResource,
  options: { method: TMethod; bodySchema: TBodySchema; responseSchema: TResponseSchema; querySchema?: TQuerySchema },
): EndpointConfig<TResource, TMethod, TResponseSchema, TBodySchema, TQuerySchema>;

export function defineEndpoint<
  TResource extends ResourceConfig<string, z.ZodType<unknown, ResourceParams<string>>, ResourceScope>,
  TMethod extends HttpMethod,
  TResponseSchema extends z.ZodType,
  TBodySchema extends z.ZodType | undefined,
  TQuerySchema extends QuerySchema | undefined = undefined,
>(
  resource: TResource,
  options: { method?: TMethod; bodySchema?: TBodySchema; responseSchema: TResponseSchema; querySchema?: TQuerySchema },
): EndpointConfig<TResource, TMethod, TResponseSchema, TBodySchema, TQuerySchema> {
  return {
    resource,
    method: options.method ?? "GET",
    responseSchema: options.responseSchema,
    ...(options.bodySchema && { bodySchema: options.bodySchema }),
    ...(options.querySchema && { querySchema: options.querySchema }),
  } as EndpointConfig<TResource, TMethod, TResponseSchema, TBodySchema, TQuerySchema>;
}

// ==================== expandEndpoint ====================

/**
 * Path params type based on scope.
 * - Camera scope: BasePathParams (tenantId + optional siteId/assetCameraKey)
 * - Tenant scope: TenantOnlyPathParams (just tenantId)
 */
export type ScopedPathParams<TScope extends ResourceScope> =
  TScope extends "tenant" ? TenantOnlyPathParams : BasePathParams;

/**
 * Builds a client API URL from an endpoint config and params.
 *
 * Supports URI templates in resource (e.g., "snapshots/{snapshotId}").
 * If template has params, resourcePathSchema is required and must encode to matching wire type.
 *
 * Uses schema.encode() to convert memory types to wire format,
 * then constructs the URL from wire strings.
 *
 * URL structure depends on resource scope:
 * - Camera scope (default): /v2/tenants/{tenant}/features/{feature}/sites/{site}/cameras/{camera}/{resource}
 * - Tenant scope: /v2/tenants/{tenant}/features/{feature}/{resource}
 */
export function expandEndpoint<
  TResource extends string,
  TResourcePathSchema extends z.ZodType<unknown, ResourceParams<TResource>>,
  TScope extends ResourceScope,
  TMethod extends HttpMethod,
  TResponseSchema extends z.ZodType,
  TBodySchema extends z.ZodType | undefined,
  TQuerySchema extends QuerySchema | undefined,
>(
  endpoint: EndpointConfig<
    ResourceConfig<TResource, TResourcePathSchema, TScope>,
    TMethod,
    TResponseSchema,
    TBodySchema,
    TQuerySchema
  >,
  params: {
    path: ScopedPathParams<TScope> & z.output<TResourcePathSchema>;
    query?: (TQuerySchema extends QuerySchema ? z.output<TQuerySchema> : undefined) | undefined;
  },
): string {
  const { resource, querySchema } = endpoint;
  const scope = resource.scope ?? "camera";

  // Encode resource path params via schema
  const resourcePath =
    resource.resourcePathSchema ?
      interpolateResource(resource.resource, resource.resourcePathSchema.encode(params.path))
    : resource.resource;

  let url: string;
  if (scope === "tenant") {
    // Tenant-scoped: no site/camera segments
    const pathWire = TenantPathParams.encode(params.path);
    url = buildFeaturePath(resource.feature, resourcePath, pathWire);
  } else {
    // Camera-scoped (default): include site/camera segments
    const pathWire = ClientApiFeaturePathParams.encode(params.path);
    url = buildClientApiPath(resource.feature, resourcePath, pathWire);
  }

  if (params.query && querySchema) {
    const queryWire = querySchema.encode(params.query);
    const qs = queryToSearchParams(queryWire);
    if (qs) url += `?${qs}`;
  }

  return url;
}
